/**
 * Inquiry API Repository
 *
 * home_karte_api との通信とデータ変換を担当
 */

import { ENV } from '../../../shared/config/env';
import { createApiClient } from '../../../shared/services/api';
import type { ApiResult } from '../../../shared/types/api';
import type { Inquiry, ProcessedSystem, SymptomLocation } from '../types';
import { statusCodeToName, isVisibleStatusCode } from '../utils/inquiryStatusMapper';
import { allInquiries } from './inquiryData';

// 環境変数からAPIベースURLを取得（未設定時はモック）
const API_BASE_URL = ENV.API_BASE_URL;
const USE_MOCK = !API_BASE_URL;

/**
 * デバッグログをFlutter WebViewに送信
 * REACT_APP_DEBUG環境変数がtrueの場合のみ出力
 */
const sendDebugLog = (message: string): void => {
  if (!ENV.DEBUG) return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).DebugChannel;
    if (webview?.postMessage) {
      webview.postMessage(message);
    }
    console.log('[Inquiry]', message);
  } catch {
    // WebView外での実行時は無視
  }
};

/**
 * API レスポンス型
 */
interface InquiryListApiResponse {
  total: number;
  page: number;
  limit: number;
  inquiries: InquiryApiItem[];
}

interface InquiryApiItem {
  inquiryId: string;
  status: number;
  inquiryType: number;
  receptionDate: string;
  visitDate: string;
  equipmentName: string;
}

interface ApiDateRange {
  start: string | null;
  end: string | null;
}

interface InquiryDetailApiResponse {
  inquiryId: string;
  status: number;
  inquiryType: number;
  companyName: string;
  receptionDate: string;
  receptionistName: string;
  visitDate: string;
  workerName: string;
  visitDesiredDate?: ApiDateRange;
  contactDesiredDate?: ApiDateRange;
  processedSystem?: string;
  inquiryDetails: InquiryDetailItem[];
}

interface InquiryDetailItem {
  inquiryDetailId: string;
  planName: string;
  planPrice: number;
  issueName: string;
  equipmentName: string;
  equipmentLocationName: string;
}

/**
 * APIレスポンスをフロント用の型に変換
 *
 * ステータスコード→表示名の変換は `utils/inquiryStatusMapper.ts` に集約。
 * 表示対象判定は `getAll` で `isVisibleStatusCode` により行う（現状は全コード表示）。
 */
const transformApiItem = (item: InquiryApiItem): Inquiry => {
  // 未知のステータスコード（マッピング未定義）を検知可能にする。
  // 過去バグ: API enum 拡張時にマッピング更新を見落とすと「受付中」誤表示で
  // 気づきにくいため、観測ログで早期発見できるようにする。
  if (!statusCodeToName[item.status]) {
    sendDebugLog(`transformApiItem: 未知のステータスコード code=${item.status} inquiryId=${item.inquiryId}`);
  }
  return {
    id: item.inquiryId,
    status: statusCodeToName[item.status] || '受付済',
    requestDate: item.receptionDate,
    visitDate: item.visitDate || '日程調整中',
    equipmentName: item.equipmentName || '',
  };
};

/**
 * 金額をフォーマット（例: 10000 → "10,000円"）
 */
const formatAmount = (price: number | null | undefined): string | undefined => {
  if (price == null) return undefined;
  return `${price.toLocaleString()}円`;
};

/**
 * 詳細APIレスポンスをフロント用の型に変換
 */
const transformApiDetail = (detail: InquiryDetailApiResponse): Inquiry => {
  const symptomLocations: SymptomLocation[] = detail.inquiryDetails.map(d => ({
    symptom: d.issueName,
    location: d.equipmentName,
  }));

  // 最初のinquiryDetailからプラン情報を取得
  const firstDetail = detail.inquiryDetails[0];
  const plan = firstDetail?.planName || undefined;
  const amount = formatAmount(firstDetail?.planPrice);

  // 未知のステータスコードを観測ログで早期検知（transformApiItem と同じ理由）
  if (!statusCodeToName[detail.status]) {
    sendDebugLog(`transformApiDetail: 未知のステータスコード code=${detail.status} inquiryId=${detail.inquiryId}`);
  }

  return {
    id: detail.inquiryId,
    status: statusCodeToName[detail.status] || '受付済',
    requestDate: detail.receptionDate,
    visitDate: detail.visitDate || '日程調整中',
    symptomLocations,
    contractor: detail.companyName,
    receptionManager: detail.receptionistName,
    manager: detail.workerName,
    contactDesiredDate: detail.contactDesiredDate,
    visitDesiredDate: detail.visitDesiredDate,
    plan,
    amount,
    processedSystem: (detail.processedSystem || '') as ProcessedSystem,
  };
};

/**
 * ページネーション付き一覧取得の戻り値
 */
interface PaginatedResult {
  inquiries: Inquiry[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Inquiry Repository
 */
export const inquiryRepository = {
  /**
   * 問い合わせ一覧を取得（ページネーション対応）
   */
  getAll: async (homeId: string, token?: string, page = 1, limit = 10): Promise<ApiResult<PaginatedResult>> => {
    if (USE_MOCK) {
      // モックモード
      sendDebugLog(`getAll: モックモード使用 (API_BASE_URL未設定)`);
      await new Promise(resolve => setTimeout(resolve, 300));
      const start = (page - 1) * limit;
      const paginatedItems = allInquiries.slice(start, start + limit);
      sendDebugLog(`getAll: モックデータ返却 (${paginatedItems.length}件, page=${page})`);
      return { success: true, data: { inquiries: paginatedItems, total: allInquiries.length, page, limit } };
    }
    sendDebugLog(`getAll: API呼出 homeId=${homeId}, page=${page}, limit=${limit}, tokenあり=${!!token}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<InquiryListApiResponse>(
      `/api/v1/inquiries?homeId=${homeId}&page=${page}&limit=${limit}`
    );

    if (!result.success) {
      sendDebugLog(`getAll: APIエラー ${JSON.stringify(result.error)}`);
      return result;
    }

    sendDebugLog(`getAll: APIレスポンス total=${result.data.total}, page=${result.data.page}`);
    // 表示対象ステータスのみ抽出（現状は全コード表示。将来非表示が必要になれば
    // isVisibleStatusCode で除外する）。カルテ Flutter (InquiryRepositoryImpl) と整合。
    const inquiries = result.data.inquiries
      .filter((item) => isVisibleStatusCode(item.status))
      .map(transformApiItem);
    return { success: true, data: { inquiries, total: result.data.total, page: result.data.page, limit: result.data.limit } };
  },

  /**
   * 問い合わせ詳細を取得
   */
  getById: async (inquiryId: string, token?: string): Promise<ApiResult<Inquiry | null>> => {
    if (USE_MOCK) {
      // モックモード
      sendDebugLog(`getById: モックモード使用 (API_BASE_URL未設定)`);
      await new Promise(resolve => setTimeout(resolve, 200));
      const inquiry = allInquiries.find(i => i.id === inquiryId);
      sendDebugLog(`getById: モックデータ返却 inquiryId=${inquiryId} found=${!!inquiry}`);
      return { success: true, data: inquiry || null };
    }
    sendDebugLog(`getById: API呼出 inquiryId=${inquiryId}, tokenあり=${!!token}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<InquiryDetailApiResponse>(
      `/api/v1/inquiries/${inquiryId}`
    );

    sendDebugLog(`getById: API結果 success=${result.success}`);

    if (!result.success) {
      sendDebugLog(`getById: APIエラー ${JSON.stringify(result.error)}`);
      return result;
    }

    sendDebugLog(`getById: APIレスポンス ${JSON.stringify(result.data)}`);
    // 未知ステータスコードの検知は transformApiDetail 内のログで実施する。
    const inquiry = transformApiDetail(result.data);
    return { success: true, data: inquiry };
  },
};
